/**
 * Date formatting util — Stage 70.1.
 *
 * The single entry point for rendering dates in the UI. Replaces the
 * scattered `new Date(...).toLocale*String(...)` calls (the Stage 70 lint
 * warned on these; Stage 70.1 promotes that rule to an error).
 *
 * `formatDate(value, format, opts?)`:
 *   - Accepts ISO strings, Date objects, or epoch milliseconds.
 *   - Null / undefined / empty / invalid input -> '' (never throws).
 *   - Default timezone is the browser's; `opts.tz` forces an explicit zone
 *     (e.g. 'UTC' for the Stage 66 activity-heatmap rule).
 *
 * THE DATE ORDER. A library has ONE date order (utils/date-order.ts), shared
 * by what its date boxes accept and what its screens show, so the two can
 * never disagree. Every format below that shows BOTH a day and a month
 * follows `getDateOrder()`; the formats with no day (month-year, weekday,
 * ...), the ISO formats and the time-only format never change.
 *
 * Named formats (month first, then day first):
 *   short         -> Jun 14, 2026         | 14 Jun 2026
 *   medium        -> Jun 14, 2026 · 10:23 AM | 14 Jun 2026 · 10:23 AM
 *   long          -> Sunday, June 14, 2026 | Sunday 14 June 2026
 *   time          -> 10:23 AM
 *   relative      -> 2h ago / 3d ago / Jun 14 or 14 Jun (older than 30 days)
 *   iso-day        -> 2026-06-14            (stable sort/storage key, any order)
 *   iso-full       -> 2026-06-14T10:23:00Z  (UTC, any order)
 *   day-month      -> Jun 14 | 14 Jun       (compact, no year)
 *   weekday        -> Sunday                 (bare day name — calendar-card headers)
 *   month-abbr     -> Jun                    (bare month — calendar-card sub-lines)
 *   weekday-short  -> Sun, Jun 14 | Sun 14 Jun
 *   month-year     -> June 2026             (calendar headers)
 *   month-year-abbr-> Jun 2026              (compact ranges in a table cell)
 *   full-date     -> June 14, 2026 | 14 June 2026 (day and full month and year:
 *                                            for a date written in a sentence or table)
 *
 * The last three were added in Stage 70.1 to "match the patterns used
 * today" (brief Part 5) so every toLocaleDate/TimeString site could move
 * onto this util and the lint promote to a zero-exception error.
 */

import { getDateOrder } from './date-order.js';

export type DateFormat =
	| 'short'
	| 'medium'
	| 'long'
	| 'time'
	| 'relative'
	| 'iso-day'
	| 'iso-full'
	| 'day-month'
	| 'weekday'
	| 'month-abbr'
	| 'weekday-short'
	| 'month-year'
	| 'month-year-abbr'
	| 'full-date';

export interface FormatDateOpts {
	tz?: string;
	/** Reference point for 'relative' (defaults to now). Injectable for tests. */
	now?: Date;
}

/** The formats that are meaningful for a value with no time component.
 *  Deliberately excludes 'medium' / 'time' / 'iso-full' / 'relative' — see
 *  `formatDateOnly`. */
export type DateOnlyFormat = Extract<
	DateFormat,
	'short' | 'long' | 'iso-day' | 'day-month' | 'weekday-short' | 'month-year' | 'month-year-abbr' | 'full-date'
>;

/**
 * Render a DATE-ONLY value — a Postgres `date` column, or any ISO
 * `YYYY-MM-DD` string that names a calendar day rather than an instant.
 *
 * WHY THIS EXISTS RATHER THAN A `{ tz: 'UTC' }` ARGUMENT YOU MUST REMEMBER.
 * A date column arrives as `2026-07-01T00:00:00Z`. Rendered in the browser's
 * zone, that instant is the *previous* evening anywhere west of Greenwich, so
 * a contract term starting 1 July renders as "Jun 30" in New York — and with
 * a timestamp format it also grows a clock time that does not exist in the
 * data ("Jun 30, 2026 · 8:00 PM"). Both were shipped on the billing surface.
 *
 * Two things make the mistake unrepresentable here rather than merely
 * discouraged: the zone is forced to UTC (so the calendar day is the one
 * that was stored, in every reader's zone), and the format union has no
 * member that can emit a time.
 *
 * Use it for: contract terms, service periods, effective/invoice/due dates,
 * schedule periods. Do NOT use it for a `timestamptz` — a real instant
 * (created_at, closed_at) *should* localise, and `formatDate(…, 'medium')`
 * is right for those.
 */
export function formatDateOnly(
	value: string | Date | number | null | undefined,
	format: DateOnlyFormat = 'short',
): string {
	return formatDate(value, format, { tz: 'UTC' });
}

function toDate(value: string | Date | number | null | undefined): Date | null {
	if (value === null || value === undefined || value === '') return null;
	const d = value instanceof Date ? value : new Date(value);
	return Number.isNaN(d.getTime()) ? null : d;
}

function pad2(n: number): string {
	return n < 10 ? `0${n}` : String(n);
}

function isoDay(d: Date, tz?: string): string {
	// Build the calendar parts in the requested zone (default local).
	const parts = new Intl.DateTimeFormat('en-CA', {
		timeZone: tz,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	}).formatToParts(d);
	const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
	return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * A calendar date in the library's order. Built from en-US parts so the
 * words stay English and only the arrangement moves:
 *   month first: "Jun 14, 2026", "Sunday, June 14, 2026", "Jun 14", "Sun, Jun 14"
 *   day first:   "14 Jun 2026",  "Sunday 14 June 2026",    "14 Jun", "Sun 14 Jun"
 */
function orderedDate(
	d: Date,
	tz: string | undefined,
	o: { weekday?: 'short' | 'long'; month: 'short' | 'long'; year: boolean },
): string {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone: tz,
		weekday: o.weekday,
		month: o.month,
		day: 'numeric',
		year: o.year ? 'numeric' : undefined,
	}).formatToParts(d);
	const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
	const [weekday, month, day, year] = [get('weekday'), get('month'), get('day'), get('year')];
	if (getDateOrder() === 'dmy') {
		return [weekday, day, month, year].filter(Boolean).join(' ');
	}
	const monthDay = `${month} ${day}`;
	const datePart = year ? `${monthDay}, ${year}` : monthDay;
	return weekday ? `${weekday}, ${datePart}` : datePart;
}

function relative(d: Date, now: Date): string {
	const diffMs = now.getTime() - d.getTime();
	const future = diffMs < 0;
	const abs = Math.abs(diffMs);
	const min = Math.floor(abs / 60_000);
	const hr = Math.floor(abs / 3_600_000);
	const day = Math.floor(abs / 86_400_000);
	const suffix = (s: string) => (future ? `in ${s}` : `${s} ago`);
	if (abs < 45_000) return 'just now';
	if (min < 60) return suffix(`${min}m`);
	if (hr < 24) return suffix(`${hr}h`);
	if (day <= 30) return suffix(`${day}d`);
	// Older than 30 days: fall back to a calendar date (drop the year if
	// it's the same year as the reference point).
	const sameYear = d.getFullYear() === now.getFullYear();
	return orderedDate(d, undefined, { month: 'short', year: !sameYear });
}

const tzCache = new Map<string, string | undefined>();

/**
 * Turns a stored library time zone into something `Intl.DateTimeFormat` accepts,
 * following the backend's `internal/clock/location.go` `ResolveLocation` rule for
 * rule. The control panel's `clientsV2.timezone_string` is usually a bare hours
 * offset ("-5", "0", "10", "5.5"), never an IANA name, and Intl throws a
 * RangeError on it, which froze any render that passed it through.
 *
 *   - empty: no zone (the browser's own, as before)
 *   - an IANA name: as is
 *   - a number from -12 to 14: a fixed offset. Whole hours become `Etc/GMT` with
 *     the sign inverted ("-5" is "Etc/GMT+5"); half hours become "+05:30" style
 *   - anything else: no zone, with one console warning per value; never a throw
 */
export function resolveTimeZone(tz: string | null | undefined): string | undefined {
	const trimmed = (tz ?? '').trim();
	if (trimmed === '') return undefined;
	if (tzCache.has(trimmed)) return tzCache.get(trimmed);
	const resolved = resolveTimeZoneUncached(trimmed);
	tzCache.set(trimmed, resolved);
	return resolved;
}

function intlAccepts(zone: string): boolean {
	try {
		new Intl.DateTimeFormat('en-US', { timeZone: zone });
		return true;
	} catch {
		return false;
	}
}

function resolveTimeZoneUncached(trimmed: string): string | undefined {
	// A library's zone is a name such as America/New_York. A bare number (not
	// stored for any library, but accepted) is checked first: some engines read
	// "5" or "-5" as an offset zone of their own, which is not what it means.
	const n = /^[+-]?\d+(\.\d+)?$/.test(trimmed) ? Number(trimmed) : NaN;
	if (Number.isFinite(n)) {
		if (n >= -12 && n <= 14) {
			let zone: string;
			if (Number.isInteger(n)) {
				zone = n === 0 ? 'Etc/GMT' : `Etc/GMT${n < 0 ? '+' : '-'}${Math.abs(n)}`;
			} else {
				const total = Math.round(Math.abs(n) * 60);
				zone = `${n < 0 ? '-' : '+'}${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
			}
			if (intlAccepts(zone)) return zone;
		}
	} else if (intlAccepts(trimmed)) {
		return trimmed;
	}
	console.warn(`formatDate: unusable time zone ${JSON.stringify(trimmed)}, using the browser's own`);
	return undefined;
}

export function formatDate(
	value: string | Date | number | null | undefined,
	format: DateFormat,
	opts: FormatDateOpts = {},
): string {
	const d = toDate(value);
	if (!d) return '';
	const tz = resolveTimeZone(opts.tz);

	switch (format) {
		case 'short':
			return orderedDate(d, tz, { month: 'short', year: true });
		case 'medium': {
			const date = orderedDate(d, tz, { month: 'short', year: true });
			const time = new Intl.DateTimeFormat('en-US', {
				timeZone: tz,
				hour: 'numeric',
				minute: '2-digit',
			}).format(d);
			return `${date} · ${time}`;
		}
		case 'long':
			return orderedDate(d, tz, { weekday: 'long', month: 'long', year: true });
		case 'time':
			return new Intl.DateTimeFormat('en-US', {
				timeZone: tz,
				hour: 'numeric',
				minute: '2-digit',
			}).format(d);
		case 'relative':
			return relative(d, opts.now ?? new Date());
		case 'day-month':
			return orderedDate(d, tz, { month: 'short', year: false });
		case 'weekday':
			// Bare day name for calendar-style day cards (the roster WAG):
			// the card carries the number separately, so a combined
			// "Mon, Aug 17" header would print the date twice.
			return new Intl.DateTimeFormat('en-US', {
				timeZone: tz,
				weekday: 'long',
			}).format(d);
		case 'month-abbr':
			return new Intl.DateTimeFormat('en-US', {
				timeZone: tz,
				month: 'short',
			}).format(d);
		case 'weekday-short':
			return orderedDate(d, tz, { weekday: 'short', month: 'short', year: false });
		case 'month-year':
			return new Intl.DateTimeFormat('en-US', {
				timeZone: tz,
				month: 'long',
				year: 'numeric',
			}).format(d);
		case 'month-year-abbr':
			// "Jun 2026". For a range in a table cell, where the day is
			// noise and the full month name is too wide to scan.
			return new Intl.DateTimeFormat('en-US', {
				timeZone: tz,
				month: 'short',
				year: 'numeric',
			}).format(d);
		case 'full-date':
			return orderedDate(d, tz, { month: 'long', year: true });
		case 'iso-day':
			return isoDay(d, tz);
		case 'iso-full': {
			// Always UTC, second precision, Z-suffixed.
			return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}T${pad2(
				d.getUTCHours(),
			)}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}Z`;
		}
		default:
			return '';
	}
}

/**
 * The viewer's own calendar day as YYYY-MM-DD, for defaulting a date
 * picker to "today". Local, never `toISOString()`, which is the UTC date
 * and reads tomorrow from early evening in the Americas.
 */
export function localTodayIso(now: Date = new Date()): string {
	const pad = (n: number) => String(n).padStart(2, '0');
	return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
